import {
  AddressValidatedAddressJsonldAddressRead,
  AddressValidatedJsonldAddressValidatedPostWrite,
  AddressValidatedJsonldAddressValidatedRead,
  HttpResponse,
} from '@/api/generated';
import Button, { BUTTON_VARIANTS } from '@/atoms/Button';
import Checkbox from '@/atoms/forms/checkbox';
import TextInput from '@/atoms/forms/Input/Text/Input';
import Form from '@/molecules/Form';
import { USER_ROLE_API_REF } from '@/types/modifiers/references';
import * as Dialog from '@radix-ui/react-dialog';
import useUserApi from 'hooks/UserApi/useUserApi';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useAddressResultForm from './hook';
import PhoneNumber from '@/atoms/forms/Input/PhoneNumber/PhoneNumber';

type SelectedAddressProps = {
  validationResult: AddressValidatedJsonldAddressValidatedRead;
  label: string;
};

const SelectedAddress = ({ label, validationResult }: SelectedAddressProps) => (
  <div className="border-primary-500 rounded border-4 text-center p-4">
    <p className="md-bold-caps">{label}</p>
    <div>
      <p>{validationResult.street}</p>
      <p>
        {validationResult.zipCode} {validationResult.city} {validationResult.region} {validationResult.countryCode}
      </p>
    </div>
  </div>
);

const ResultForm = ({
  validationResult,
  handleCreateAddress,
}: {
  validationResult: AddressValidatedJsonldAddressValidatedRead;
  handleCreateAddress: (
    data: AddressValidatedJsonldAddressValidatedPostWrite,
    responseId: string,
  ) => Promise<HttpResponse<AddressValidatedAddressJsonldAddressRead, void>>;
}) => {
  const { t } = useTranslation();
  const { formMethods, onSubmit } = useAddressResultForm({
    handleCreateAddress,
    responseId: validationResult.responseId || '',
  });

  const { user } = useUserApi();
  const isPro = user?.roles?.includes(USER_ROLE_API_REF.ROLE_SHOP_OWNER);

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={onSubmit}>
        <div>
          <p>{t('address.modal.createAddress.confirmLabel')}</p>
          {!validationResult.fieldWasModify ? (
            <SelectedAddress
              label={t('address.modal.createAddress.enteredValue')}
              validationResult={validationResult}
            />
          ) : (
            <>
              <div className="bg-mercury-50 text-center p-4 mb-4">
                <p className="md-bold-caps">{t('address.modal.createAddress.enteredValue')}</p>
                <p className="max-w-80 m-auto">{validationResult.entered}</p>
              </div>
              <SelectedAddress label={t('modal.createAddress.recommendedValue')} validationResult={validationResult} />
            </>
          )}
          <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <TextInput label="firstName" name="firstName" placeholder="firstName" type="text" required />
              <TextInput label="lastName" name="lastName" placeholder="lastName" type="text" required />
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <TextInput
                label="additionalAddress"
                name="additionalAddress"
                placeholder="additionalAddress"
                type="text"
              />
              <PhoneNumber label="phone" name="phone" placeholder="phone" type="text" required />
            </div>
            {!isPro && (
              <>
                <Checkbox label="isDefaultShipping" name="isDefaultShipping" placeholder="isDefaultShipping" />
                <Checkbox label="isDefaultBilling" name="isDefaultBilling" placeholder="isDefaultBilling" />
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col md:flex-row pb-4 gap-3 bg-neutral-50 mt-4 rounded-b-lg">
          <Dialog.DialogClose asChild>
            <Button className="grow" type="button" variant={BUTTON_VARIANTS.SECONDARY}>
              {t('cancel')}
            </Button>
          </Dialog.DialogClose>
          <Dialog.DialogClose asChild>
            <Button className="grow" disabled={formMethods.formState.isSubmitting} onClick={onSubmit} type="button">
              {t('validate')}
            </Button>
          </Dialog.DialogClose>
        </div>
      </Form>
    </FormProvider>
  );
};

export default ResultForm;
